import { useCallback, useRef } from 'react';
import { Button, CenterScreenModal } from '../atoms'
import {
  useContractDetailsContext,
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUpdateContractFormModalContext,
  useUserLanguage,
} from '@/customHooks'
import { Col, Row } from 'react-grid-system';
import {
  ContractStatusOption,
  UpdateContractInput,
  useUpdateContractMutation,
} from '@/graphql/generated';
import { NormalFormSelectionField } from '../molecules';
import { contractStatusMapper } from '@/helpers';

const textsData = {
  en: {
    title: 'Update Contract',
    confirmation: {
      update: {
        title: 'Contract has been updated',
        description: (id: string) => `Contract ${id} has been updated succesfully`,
      },
    },
    button: {
      confirm: 'Confirm',
      cancel: 'Cancel'
    }
  },
  es: {
    title: 'Actualizar un Contrato',
    confirmation: {
      update: {
        title: 'Contrato actualizado con éxito',
        description: (id: string) => `El Contrato ${id} fue actualizada con éxito`,
      },
    },
    button: {
      confirm: 'Aceptar',
      cancel: 'Volver'
    }
  }
}

export const UpdateContractFormModal = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const contractDataRef = useRef<UpdateContractInput | null>(null)

  const [updateContract, { loading: isContractLoading }] = useUpdateContractMutation()

  const { isOpen, closeModal } = useUpdateContractFormModalContext()

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { contract, setContract } = useContractDetailsContext()

  const { open: errorHandle } = useErrorHandlerContext()

  const statusOptions = Object.values(ContractStatusOption).map(value => ({
    name: contractStatusMapper(value, userLanguage),
    value: value,
  }));

  const onChangeHandle = useCallback((value: ContractStatusOption | null) => {
    if (!value) return;
    contractDataRef.current = {
      statusOption: value,
    };
  }, []);

  const onConfirmHandle = () => {
    if (!contractDataRef.current || !contract?.id) return

    updateContract({
      variables: {
        contractId: contract.id,
        input: contractDataRef.current
      }
    })
      .then((response) => {
        closeModal()
        confirmDialogModalOpen({
          title: texts.confirmation.update.title,
          description: texts.confirmation.update.description(response.data?.updateContract?.id ?? ''),
        })
        setContract(undefined) // Forzar el refetch de los datos del contrato
      })
      .catch(errorHandle)
  }

  return (
    <CenterScreenModal
      isOpen={isOpen && !!contract}
      onClose={closeModal}
    >
      <Row>
        <Col>
          <h2>{texts.title}</h2>
        </Col>
      </Row>
      <NormalFormSelectionField<ContractStatusOption>
        label="Estado del Contrato"
        name="contractStatus"
        options={statusOptions}
        value={contract?.statusOption}
        onChange={onChangeHandle}
      />
      <br />
      <Row justify="center" gutterWidth={32}>
        <Col xs="content">
          <Button secondary onClick={() => closeModal()} disabled={isContractLoading}>
            {texts.button.cancel}
          </Button>
        </Col>
        <Col xs="content">
          <Button
            onClick={() => onConfirmHandle()}
            isLoading={isContractLoading}
          >
            {texts.button.confirm}
          </Button>
        </Col>
      </Row>
    </CenterScreenModal>
  )
}
